import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import Uploadimg from "../../assets/img/uploaddocoment.png";
import { haveValue, toast } from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import Cancelimg from "../../assets/img/cancel-01.svg";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import SkorProtectShield from "../../assets/img/skor-protect-shield.png";
import * as pdfjsLib from "pdfjs-dist";
import AuthContext from "../../context/auth/AuthContext";
import PhoneInput from "react-phone-input-2";
import LoadingSpinner from "../LoadingSpinner";
import Select from "react-select";
import provincesData from "../../Utils/provincia_municipios.json";
import streetTypeCodes from "../../Utils/streetTypeCodes.json";
import InsuranceReviewModal from "./InsuranceReviewModal";
import { OutlineShieldIcon, DownloadIcon } from "../../helper/svgHelper";
import moreInfoimg from "../../assets/img/moreInfoimg.svg";
import { Tooltip as ReactTooltip } from "react-tooltip";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
];
const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB

const INSURANCE_COVERED_OPTIONS = [
  { title: "myself", icon: "/images/user-icon.svg", key: "myself" },
  {
    title: "someoneElse",
    icon: "/images/multiple-user.svg",
    key: "someoneElse",
  },
];

const PERSON_TYPE_OPTIONS = [
  { title: "individual", icon: "/images/user-icon.svg", key: "individual" },
  { title: "company", icon: "/images/building-icon.svg", key: "company" },
];

const DOCUMENT_TYPE_OPTIONS = [
  { label: "selectDocumentType", value: "" },
  { label: "dni", value: "dni" },
  { label: "nie", value: "nie" },
  { label: "passport", value: "passport" },
];

// Fields shown when insurance_covered === "myself"
const FIELDS_MYSELF = [
  {
    label: "documentType",
    type: "select",
    options: DOCUMENT_TYPE_OPTIONS,
    name: "document_type",
  },
  {
    label: "documentNumber",
    placeholder: "enterDocumentNumber",
    name: "document_number",
  },
];

// Fields shown when insurance_covered === "someoneElse" AND person_type === "individual"
const FIELDS_INDIVIDUAL = [
  {
    label: "documentType",
    type: "select",
    options: DOCUMENT_TYPE_OPTIONS,
    name: "document_type",
  },
  {
    label: "documentNumber",
    placeholder: "enterDocumentNumber",
    name: "document_number",
  },
  { label: "firstName", placeholder: "enterFirstName", name: "first_name" },
  { label: "lastName", placeholder: "enterLastName", name: "last_name" },
  { label: "email", placeholder: "enterEmail", name: "email", required: false },
  { label: "phoneNumber", name: "phone_number", required: false },
];

// Fields shown when insurance_covered === "someoneElse" AND person_type === "company"
// document_type is hidden (CIF is implicit); document_number relabelled; first_name → company_name
const FIELDS_COMPANY = [
  { label: "enterCif", placeholder: "enterCif", name: "document_number" },
  { label: "companyName", placeholder: "enterCompanyName", name: "first_name" },
  { label: "email", placeholder: "enterEmail", name: "email", required: false },
  { label: "phoneNumber", name: "phone_number", required: false },
];

const INITIAL_INSURANCE_DETAILS = {
  insurance_covered: "myself",
  gran_tenedor: false,
  lease_start_date: "",
  policy_start_date: "",
};

let _bId = 0;
const createBeneficiary = () => ({
  id: ++_bId,
  person_type: "individual",
  document_type: "",
  document_number: "",
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  phone_number_full: "",
  province: "",
  city: "",
  street_name: "",
  street_type: "",
  street_number: "",
  block: "",
  ladder: "",
  floor: "",
  door: "",
  postcode: "",
  additional_address: "",
});

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const getTodayStr = () => {
  const local = new Date();
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
  return local.toJSON().slice(0, 10);
};

const formatSize = (size) => {
  const kb = size / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(2)} MB`;
};

const dateToInputValue = (dateStr) =>
  dateStr?.includes("/") ? dateStr.split("/").reverse().join("-") : dateStr;

const inputValueToDate = (value) =>
  value ? value.split("-").reverse().join("/") : "";

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────
const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-trash2 w-4 h-4"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" x2="10" y1="11" y2="17" />
    <line x1="14" x2="14" y1="11" y2="17" />
  </svg>
);

const AlertIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-circle-alert w-5 h-5 text-destructive"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </svg>
);

const ToggleButton = ({ value, toggleKey, icon, title, handleClick, _t }) => (
  <div
    className={`toggle d-flex cursor-pointer align-items-center flex-column justify-content-center ${
      value === toggleKey ? "toggle-selected" : ""
    }`}
    onClick={handleClick}
  >
    <img src={icon} alt="User" />
    <h5>{_t(title)}</h5>
  </div>
);

// ─────────────────────────────────────────────
// AddressField
// ─────────────────────────────────────────────
const AddressField = ({
  _t,
  insuranceDetails,
  setInsuranceDetails,
  isExpanded,
  setIsExpanded,
}) => {
  const compactSelectStyles = (hasValue) => ({
    control: (base) => ({
      ...base,
      minHeight: "none",
      height: "var(--select-height, 28px)",
      border: "0",
      boxShadow: "none",
      fontSize: "var(--select-font-size, 0.972vw)",
      padding: "0",
      cursor: "pointer",
      backgroundColor: "transparent",
      borderRadius: "0",
      "&:hover": { border: "0" },
    }),
    valueContainer: (base) => ({
      ...base,
      height: "var(--select-height, 28px)",
      padding: "0",
      margin: "0",
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      overflow: "hidden",
    }),
    singleValue: (base) => ({
      ...base,
      margin: 0,
      color: "#000",
      gridArea: "1/1/2/3",
    }),
    placeholder: (base) => ({
      ...base,
      margin: 0,
      color: "#7f7f7f",
      gridArea: "1/1/2/3",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      gridArea: "1/1/2/3",
    }),
    indicatorsContainer: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInsuranceDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, selected) => {
    setInsuranceDetails((prev) => ({
      ...prev,
      [name]: selected?.value || "",
      ...(name === "province" ? { city: "" } : {}),
    }));
  };

  const numericOnlyOnKeyDown = (e) => {
    const allowed = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Enter",
    ];
    if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
  };

  const isAddressIncomplete =
    !insuranceDetails.province ||
    !insuranceDetails.city ||
    !insuranceDetails.street_name ||
    !insuranceDetails.street_type ||
    !insuranceDetails.street_number ||
    !insuranceDetails.postcode;

  if (!isExpanded) {
    return (
      <div className="main-form-group full-width">
        <div
          className={`form-group addr-collapsed-box d-flex flex-row align-items-center justify-content-between cursor-pointer ${isAddressIncomplete ? "input-error" : ""}`}
          onClick={() => setIsExpanded(true)}
        >
          <div className="d-flex flex-column">
            <label>
              {_t("address")} <span className="required-star">*</span>
            </label>
            <input
              className="addr-placeholder"
              value={
                insuranceDetails.street_name
                  ? `${insuranceDetails.street_name}${
                      insuranceDetails.street_number
                        ? ", " + insuranceDetails.street_number
                        : ""
                    }`
                  : _t("clickToEnterAddress")
              }
              readOnly
            />
          </div>
          <img
            src="/images/editGreen.svg"
            alt="edit"
            className="addr-edit-icon"
          />
        </div>
      </div>
    );
  }

  const cityOptions = insuranceDetails.province
    ? provincesData
        .find((p) => p.Provincia === insuranceDetails.province)
        ?.municipios.map((m) => ({ label: m, value: m })) || []
    : [];

  const streetTypeValue = insuranceDetails.street_type
    ? (() => {
        const foundKey = Object.keys(streetTypeCodes).find(
          (key) => streetTypeCodes[key] === insuranceDetails.street_type
        );
        return {
          label: foundKey
            ? foundKey.charAt(0).toUpperCase() + foundKey.slice(1)
            : insuranceDetails.street_type,
          value: insuranceDetails.street_type,
        };
      })()
    : null;

  return (
    <div className="main-form-group full-width">
      <h5 className="address-section-heading">{_t("address")}</h5>
      <div className="addr-expanded-card">
        <div className="form-wrapper mt-0">
          {/* Province */}
          <div className="main-form-group" id="custom-select-search">
            <div className="form-group">
              <label>
                {_t("province")} <span className="required-star">*</span>
              </label>
              <Select
                classNamePrefix="react-select"
                name="province"
                value={
                  insuranceDetails.province
                    ? {
                        label: insuranceDetails.province,
                        value: insuranceDetails.province,
                      }
                    : null
                }
                options={provincesData.map((p) => ({
                  label: p.Provincia,
                  value: p.Provincia,
                }))}
                placeholder={_t("selectProvince")}
                styles={compactSelectStyles(!!insuranceDetails.province)}
                onChange={(selected) =>
                  handleSelectChange("province", selected)
                }
                isSearchable
                required
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
              />
              <img
                src="/images/down-arrow-grey.svg"
                className="downArrow"
                alt="arrow"
              />
            </div>
          </div>

          {/* Municipality */}
          <div
            className={`main-form-group ${!insuranceDetails.province ? "" : ""}`}
            id="custom-select-search"
          >
            <div className="form-group">
              <label>
                {_t("municipality")} <span className="required-star">*</span>
              </label>
              <Select
                classNamePrefix="react-select"
                name="city"
                value={
                  insuranceDetails.city
                    ? {
                        label: insuranceDetails.city,
                        value: insuranceDetails.city,
                      }
                    : null
                }
                options={cityOptions}
                placeholder={
                  insuranceDetails.province
                    ? _t("selectMunicipality")
                    : _t("selectProvinceFirst")
                }
                onChange={(selected) => handleSelectChange("city", selected)}
                styles={compactSelectStyles(!!insuranceDetails.city)}
                isSearchable
                isDisabled={!insuranceDetails.province}
                required
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
              />
              <img
                src="/images/down-arrow-grey.svg"
                className="downArrow"
                alt="arrow"
              />
            </div>
          </div>

          {/* Street Type */}
          <div className="main-form-group" id="custom-select-search">
            <div className="form-group">
              <label>
                {_t("typeOfRoad")} <span className="required-star">*</span>
              </label>
              <Select
                classNamePrefix="react-select"
                name="street_type"
                value={streetTypeValue}
                options={Object.keys(streetTypeCodes).map((type) => ({
                  label: type.charAt(0).toUpperCase() + type.slice(1),
                  value: streetTypeCodes[type],
                }))}
                placeholder={_t("selectType")}
                onChange={(selected) =>
                  handleSelectChange("street_type", selected)
                }
                styles={compactSelectStyles(!!insuranceDetails.street_type)}
                isSearchable
                required
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
              />
              <img
                src="/images/down-arrow-grey.svg"
                className="downArrow"
                alt="arrow"
              />
            </div>
          </div>

          {/* Street Name */}
          <div className="main-form-group">
            <div className="form-group">
              <label>
                {_t("streetName")} <span className="required-star">*</span>
              </label>
              <input
                type="text"
                name="street_name"
                placeholder={_t("eGMontesa")}
                value={insuranceDetails.street_name}
                onChange={handleInputChange}
                className={!insuranceDetails.street_name ? "input-error" : ""}
                required
              />
            </div>
          </div>

          {/* Street Number */}
          <div className="main-form-group">
            <div className="form-group">
              <label>
                {_t("number")} <span className="required-star">*</span>
              </label>
              <input
                type="text"
                name="street_number"
                placeholder={_t("eG5")}
                value={insuranceDetails.street_number}
                onChange={handleInputChange}
                onKeyDown={numericOnlyOnKeyDown}
                className={!insuranceDetails.street_number ? "input-error" : ""}
                required
              />
            </div>
          </div>

          {/* Postal Code */}
          <div className="main-form-group">
            <div className="form-group">
              <label>
                {_t("postalCode")} <span className="required-star">*</span>
              </label>
              <input
                type="text"
                name="postcode"
                value={insuranceDetails.postcode}
                placeholder="28006"
                onChange={handleInputChange}
                onKeyDown={numericOnlyOnKeyDown}
                maxLength={5}
                className={!insuranceDetails.postcode ? "input-error" : ""}
                required
              />
            </div>
          </div>
        </div>

        {/* Block / Ladder / Floor / Door */}
        <div className="addr-grid-4">
          {[
            { label: "block", name: "block", numeric: false, required: false },
            {
              label: "ladder",
              name: "ladder",
              numeric: false,
              required: false,
            },
            {
              label: "floor",
              name: "floor",
              numeric: true,
              required: false,
              max: 40,
            },
            { label: "door", name: "door", numeric: false, required: false },
          ].map(
            ({
              label,
              name,
              numeric,
              required,
              max,
              maxLength,
              placeholder,
            }) => (
              <div className="form-group" key={name}>
                <label>
                  {_t(label)}
                  {required && <span className="required-star"> *</span>}
                </label>
                <input
                  type="text"
                  name={name}
                  value={insuranceDetails[name]}
                  placeholder={placeholder}
                  onChange={handleInputChange}
                  onKeyDown={numeric ? numericOnlyOnKeyDown : undefined}
                  onInput={
                    max != null
                      ? (e) => {
                          let v = e.target.value.replace(/\D/g, "");
                          if (v !== "" && Number(v) > max) v = String(max);
                          e.target.value = v;
                        }
                      : undefined
                  }
                  maxLength={maxLength}
                  className={
                    required && !insuranceDetails[name] ? "input-error" : ""
                  }
                  required={required}
                />
              </div>
            )
          )}
        </div>

        {/* Additional Address */}
        <div className="form-wrapper additional-address addr-additional-address">
          <div className="main-form-group full-width">
            <div className="form-group">
              <label>{_t("additionalAddress")}</label>
              <input
                type="text"
                name="additional_address"
                value={insuranceDetails.additional_address}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div
          className="addr-collapse-btn d-flex align-items-center justify-content-center cursor-pointer"
          onClick={() => setIsExpanded(false)}
        >
          <img
            src="/images/down-arrow-grey.svg"
            alt="collapse"
            className={`me-2 collapse-arrow ${isExpanded ? "rotate-180" : ""}`}
          />
          <span>{_t("collapse")}</span>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// BeneficiaryCard
// ─────────────────────────────────────────────
const BeneficiaryCard = ({
  beneficiary,
  index,
  totalCount,
  onUpdate,
  onDelete,
  insuranceCovered,
  isAddrExpanded,
  setIsAddrExpanded,
  hideCard,
  _t,
}) => {
  const activeFields = (() => {
    if (insuranceCovered === "myself") return FIELDS_MYSELF;
    if (beneficiary.person_type === "company") return FIELDS_COMPANY;
    return FIELDS_INDIVIDUAL;
  })();

  // Wrap onUpdate so AddressField's functional-update pattern works
  const setBeneficiary = (updater) =>
    onUpdate(typeof updater === "function" ? updater(beneficiary) : updater);

  const mainFields = activeFields.filter(
    (f) => f.name !== "email" && f.name !== "phone_number"
  );
  const contactFields = activeFields.filter(
    (f) => f.name === "email" || f.name === "phone_number"
  );

  return (
    <div
      className={
        hideCard
          ? "d-none"
          : "purchase-insurance-form d-flex flex-column custom-gap-3"
      }
    >
      {/* Card header */}
      <div className="d-flex align-items-center justify-content-between">
        <h5 className="d-flex align-items-center custom-gap-2 mb-0">
          <img src="/images/user-icon.svg" alt="User" />
          {_t("beneficiaryInformation")}
          {totalCount > 1 ? ` ${index + 1}` : ""}
          <img
            data-tooltip-id={`beneficiary-info-tooltip-${beneficiary.id}`}
            src={moreInfoimg}
            alt="Vector Icon"
            className="vactorImg vactorImg1 default-check-img"
            style={{ filter: "none" }}
          />
          <ReactTooltip
            id={`beneficiary-info-tooltip-${beneficiary.id}`}
            noArrow
            className="react-tooltip-styling details-skor-tooltip"
            render={() => <p>{_t("insuranceBeneficiaryTooltip")}</p>}
          />
        </h5>
        <div></div>

        {(insuranceCovered === "someoneElse" || totalCount > 1) && (
          <div className="beneficiary-card-actions">
            {insuranceCovered === "someoneElse" &&
              PERSON_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className={`beneficiary-person-type-btn ${beneficiary.person_type === opt.key ? "active" : ""}`}
                  onClick={() =>
                    onUpdate({
                      person_type: opt.key,
                      document_type: opt.key === "company" ? "cif" : "",
                      document_number: "",
                      first_name: "",
                      last_name: "",
                    })
                  }
                >
                  <img src={opt.icon} alt="" />
                  {_t(opt.title)}
                </button>
              ))}
            {totalCount > 1 && index > 0 && (
              <button
                type="button"
                className="beneficiary-delete-btn"
                onClick={onDelete}
              >
                <TrashIcon />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main fields (doc type/number, names) */}
      <div className="form-wrapper">
        {mainFields.map((field) => {
          const hasValue = !!beneficiary[field.name];
          return (
            <div
              className={`main-form-group ${field?.class || ""}`}
              key={field.name}
            >
              <div className="form-group">
                <label>
                  {_t(field.label)}
                  {field.required !== false && (
                    <span className="required-star"> *</span>
                  )}
                </label>
                {field.type === "select" ? (
                  <>
                    <select
                      name={field.name}
                      value={beneficiary[field.name]}
                      onChange={(e) =>
                        onUpdate({ [field.name]: e.target.value })
                      }
                      className={`${!hasValue ? "select-empty" : ""} ${!hasValue && field.required !== false ? "input-error" : ""}`}
                    >
                      {field.options.map((option) => (
                        <option value={option.value} key={option.value}>
                          {_t(option.label)}
                        </option>
                      ))}
                    </select>
                    <img
                      src="/images/down-arrow-grey.svg"
                      className="downArrow"
                      alt="arrow"
                    />
                  </>
                ) : (
                  <input
                    type="text"
                    name={field.name}
                    placeholder={_t(field.placeholder)}
                    value={beneficiary[field.name]}
                    onChange={(e) => onUpdate({ [field.name]: e.target.value })}
                    className={
                      !hasValue && field.required !== false ? "input-error" : ""
                    }
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Address */}
      <AddressField
        _t={_t}
        insuranceDetails={beneficiary}
        setInsuranceDetails={setBeneficiary}
        isExpanded={isAddrExpanded}
        setIsExpanded={setIsAddrExpanded}
      />

      {/* Contact fields (email / phone) — only for someoneElse */}
      {contactFields.length > 0 && (
        <div className="form-wrapper">
          {contactFields.map((field) => (
            <div className="main-form-group" key={field.name}>
              <div className="form-group">
                <label>{_t(field.label)}</label>
                {field.name === "phone_number" ? (
                  <PhoneInput
                    className="phone_input_info"
                    international
                    country="es"
                    value={beneficiary.phone_number_full}
                    onChange={(value, country) => {
                      const dialCode = country?.dialCode || "";
                      const numberWithoutCode = value?.startsWith(dialCode)
                        ? value.slice(dialCode.length)
                        : value || "";
                      onUpdate({
                        phone_number_full: value,
                        phone_number: numberWithoutCode,
                      });
                    }}
                  />
                ) : (
                  <input
                    type="text"
                    name={field.name}
                    placeholder={_t(field.placeholder)}
                    value={beneficiary[field.name]}
                    onChange={(e) => onUpdate({ [field.name]: e.target.value })}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const PurchaseInsurancePopup = ({
  insuracePopupModal,
  setInsuracePopupModal,
  selectedTenants,
  property,
  rent,
  insuranceCost,
  preFilledInsuranceDocument,
  usedFor,
  onSkorProtectSuccess,
  onInsuranceSendPaymentLinkSuccess,
}) => {
  const { _t } = useTranslator();
  const { user: landlordUser } = useContext(AuthContext);
  const isSkorProtect = usedFor === "skorProtect";
  const [showInsuranceReviewModal, setShowInsuranceReviewModal] =
    useState(false);

  const [redirectLoading, setRedirectLoading] = useState(false);
  const [insuranceCheckLoading, setInsuranceCheckLoading] = useState(false);
  const [sendPaymentLinkLoading, setSendPaymentLinkLoading] = useState(false);
  const [documentUploadLoading, setDocumentUploadLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [showPaymentButton, setShowPaymentButton] = useState(false);
  const [consentErrors, setConsentErrors] = useState([]);
  const [consentLoading, setConsentLoading] = useState(false);
  const [sendLinkError, setSendLinkError] = useState(false);
  const [sendLinkLoading, setSendLinkLoading] = useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState(
    INITIAL_INSURANCE_DETAILS
  );
  const [beneficiaries, setBeneficiaries] = useState([createBeneficiary()]);
  const [addrExpandedIds, setAddrExpandedIds] = useState({});

  const updateBeneficiary = (id, updates) => {
    if (updates.email !== undefined) setSendLinkError(false);
    setBeneficiaries((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...updates } : b))
    );
  };
  const deleteBeneficiary = (id) =>
    setBeneficiaries((prev) => prev.filter((b) => b.id !== id));
  const addBeneficiary = () =>
    setBeneficiaries((prev) => [...prev, createBeneficiary()]);

  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const {
    initiateInsuracePayment,
    uploadFileData,
    checkForInsuranceEligibility,
    checkMutuaConsents,
  } = useContext(LandLordContext);

  // ── Consent check on open ──────────────────
  useEffect(() => {
    if (!insuracePopupModal || !selectedTenants?.length) {
      setConsentErrors([]);
      return;
    }
    setConsentLoading(true);
    const tenantIds = selectedTenants
      .map((t) => t?.tenantid?._id)
      .filter(Boolean);

    // Picking for first because we are only allowing user to select only one application at a time.
    const application_id = selectedTenants?.[0]?.application_id?._id;

    checkMutuaConsents(tenantIds, application_id)
      .then((res) => {
        setConsentErrors(
          res?.missingConsent?.length
            ? res.missingConsent.map(
                (t) => `${t.name} ${_t("hasMissingGrupoMutuaConsent")}`
              )
            : []
        );
      })
      .finally(() => setConsentLoading(false));
  }, [insuracePopupModal]);

  // ── File validation ────────────────────────
  const validateFiles = async (newFiles) => {
    const validated = [];
    const invalids = [];

    for (const file of newFiles) {
      const id = `${file.name}-${file.lastModified}`;

      if (!ALLOWED_TYPES.includes(file.type)) {
        invalids.push({
          id,
          name: file.name,
          reason: _t("fileTypeNotAllowed"),
        });
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        invalids.push({
          id,
          name: file.name,
          reason: _t("fileUploadedExceedsTwoPointFiveMbFileLimit"),
        });
        continue;
      }

      try {
        if (file.type === "application/pdf") {
          const arrayBuffer = await file.arrayBuffer();
          await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        } else {
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = () => reject(new Error("Corrupted image"));
            img.src = URL.createObjectURL(file);
          });
        }
        validated.push({ id, file, name: file.name, size: file.size });
      } catch (err) {
        const reason = err?.message?.toLowerCase().includes("password")
          ? _t("isPasswordProtectedPDF")
          : _t("isCorruptedOrUnreadableFile");
        invalids.push({ id, name: file.name, reason });
      }
    }

    if (invalids.length) setInvalidFiles((prev) => [...prev, ...invalids]);
    return validated;
  };

  const addFiles = async (rawFiles) => {
    if (documentFiles.length > 0) {
      toast.error(_t("onlyOneFileAllowed"));
      return;
    }
    const valid = await validateFiles([rawFiles[0]]);
    if (valid.length) setDocumentFiles(valid);
  };

  // ── Drag & drop ────────────────────────────
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files?.length) await addFiles(e.dataTransfer.files);
  };
  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  // ── Form helpers ───────────────────────────
  const isValid = () => {
    const { insurance_covered, lease_start_date, policy_start_date } =
      insuranceDetails;
    if (!insurance_covered || !lease_start_date || !policy_start_date)
      return false;
    return beneficiaries.every(
      (b) =>
        b.province &&
        b.city &&
        b.street_name &&
        b.street_type &&
        b.street_number &&
        b.postcode
    );
  };

  const propertyErrors = useMemo(() => {
    const errors = [];
    if (!property?.province) errors.push(_t("provinceIsRequired"));
    if (!property?.muncipality) errors.push(_t("municipalityIsRequired"));
    if (!property?.postcode) errors.push(_t("postcodeIsRequired"));
    if (!property?.street) errors.push(_t("streetNameIsRequired"));
    if (!property?.street_number) errors.push(_t("streetNumberIsRequired"));
    return [...errors, ...consentErrors];
  }, [property, consentErrors]);

  // ── Actions ────────────────────────────────
  const handleUploadFiles = async (fileType = "rental_contract") => {
    try {
      setDocumentUploadLoading(true);
      const formData = new FormData();
      documentFiles.forEach((f) => formData.append("landlordDocs", f.file));
      formData.append("fileType", fileType);
      formData.append("propertyId", property?._id);
      await uploadFileData(formData);
    } catch (err) {
      console.error("Error uploading files:", err);
    } finally {
      setDocumentUploadLoading(false);
    }
  };

  const handleMakePayment = async () => {
    try {
      setRedirectLoading(true);
      const application_id = selectedTenants?.[0]?.application_id?._id;

      const data = await initiateInsuracePayment(property?._id, {
        report_rent: rent,
        selected_tenants: selectedTenants.map((t) => t?.tenantid?._id),
        application_id,
      });
      if (data?.status === "success") {
        window.location.replace(data?.paymentUrl);
      }
    } catch {
      console.log("Failed to redirect to payment page");
      setRedirectLoading(false);
    }
  };

  const checkForInsurance = async ({ sendPaymentLinkToBeneficiary }) => {
    console.log("Form JSON:", { insuranceDetails, beneficiaries });

    try {
      if (sendPaymentLinkToBeneficiary) {
        setSendPaymentLinkLoading(true);
      } else {
        setInsuranceCheckLoading(true);
      }
      await handleUploadFiles();

      const application_id = selectedTenants?.[0]?.application_id?._id;
      const payload = {
        ...insuranceDetails,
        propertyId: property?._id,
        property_rent: rent,
        selected_tenants: selectedTenants.map((t) => t?.tenantid?._id),
        // Backend structure: array of beneficiary objects
        beneficiaries: beneficiaries.map(
          ({ id, phone_number_full, ...rest }) => rest
        ),
        application_id,
        ...(usedFor ? { usedFor } : {}),
        ...(sendPaymentLinkToBeneficiary
          ? { sendPaymentLink: sendPaymentLinkToBeneficiary }
          : {}),
      };

      const saveRes = await checkForInsuranceEligibility(payload);

      if (!saveRes?.success) {
        toast.error(saveRes?.message);
        setShowInsuranceReviewModal(true);
        return;
      }

      const ACCEPTED_INSURANCE_MSG =
        "Mutua de Propietarios confirma que el proyecto presentado para proceder a la contratación del seguro Mutua Alquiler Confort, ha sido aceptado, habiéndose tenido en cuenta para su aceptación, la información y documentación facilitada por el mediador.";

      if (saveRes?.success_res === ACCEPTED_INSURANCE_MSG) {
        if (isSkorProtect) {
          await onSkorProtectSuccess?.();
        } else if (sendPaymentLinkToBeneficiary) {
          await onInsuranceSendPaymentLinkSuccess?.();
        } else {
          handleMakePayment();
          toast.success(
            _t("youAreEligibleForInsuranceYouCanContinueWithPayment")
          );
        }
      } else {
        toast.error(saveRes?.success_res);
        setShowInsuranceReviewModal(true);
      }
    } catch {
      console.log("Failed to check insurance eligibility");
    } finally {
      if (sendPaymentLinkToBeneficiary) {
        setSendPaymentLinkLoading(false);
      } else {
        setInsuranceCheckLoading(false);
      }
    }
  };

  const handleSendPaymentLink = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const filledEmails = beneficiaries
      .map((b) => b.email?.trim())
      .filter(Boolean);
    if (!filledEmails.length) {
      setSendLinkError(_t("noEmailIsEnteredInBeneficiary"));
      return;
    }
    const hasInvalid = filledEmails.some((e) => !emailRegex.test(e));
    if (hasInvalid) {
      setSendLinkError("Please enter a valid email in beneficiary");
      return;
    }
    setSendLinkError(null);
    await checkForInsurance({ sendPaymentLinkToBeneficiary: true });
  };

  // ── Derived values ─────────────────────────
  const isSubmitDisabled =
    !haveValue(rent) ||
    documentFiles.length === 0 ||
    redirectLoading ||
    !isValid() ||
    propertyErrors.length > 0 ||
    consentLoading;

  const handleCoveredClick = (toggleKey) => {
    if (toggleKey === insuranceDetails.insurance_covered) return;
    setInsuranceDetails((prev) => ({ ...prev, insurance_covered: toggleKey }));
    setBeneficiaries([createBeneficiary()]);
    setAddrExpandedIds({});
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <>
      {!showInsuranceReviewModal ? (
        <Modal
          show={insuracePopupModal}
          className="metermodal wrapper-modal modal-xl custom-modal-popup purchase-insurance-modal"
          centered
          onHide={() => setInsuracePopupModal(false)}
        >
          <Modal.Header>
            <h2 className="modal-title-text">
              {isSkorProtect
                ? _t("uploadContract")
                : _t("purchaseInsuranceHeading")}
            </h2>
            <div
              className="cursor-pointer"
              onClick={() => setInsuracePopupModal(false)}
            >
              <img
                src={Cancelimg}
                alt="icon"
                className="canceicon topRightIcon"
              />
            </div>
          </Modal.Header>

          <Modal.Body>
            <div className="bodyContentset">
              <form onSubmit={(e) => e.preventDefault()}>
                {/* ── Insurance summary banner ── */}
                {!isSkorProtect && (
                  <div className="purchase-insurance-details">
                    <div className="purchase-insurance-details-info">
                      <h1>
                        {_t("twelveMonthCoverage")}
                        {" - "}
                        {insuranceCost != null ? `€${insuranceCost}` : "-"}
                        {"/"}
                        {_t("year")}
                      </h1>
                    </div>
                    {preFilledInsuranceDocument && (
                      <a
                        href={`${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}/${preFilledInsuranceDocument}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="purchase-insurance-coverage-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DownloadIcon />
                        {_t("insuranceCoverage")}
                      </a>
                    )}
                  </div>
                )}

                {/* ── Fields + overlay wrapper ── */}
                <div style={{ position: "relative" }}>
                  <div
                    style={
                      insuranceCheckLoading || sendPaymentLinkLoading
                        ? { opacity: 0.25, pointerEvents: "none" }
                        : {}
                    }
                  >
                    {/* ── Who is covered ── */}
                    <div className="purchase-insurance-details-wrapper d-flex flex-column bigform">
                      <h5>{_t("whoWillBeCoveredByThisInsurance")}</h5>
                      <div className="toggle-wrapper">
                        {INSURANCE_COVERED_OPTIONS.map((toggle) => (
                          <ToggleButton
                            key={toggle.key}
                            _t={_t}
                            title={toggle.title}
                            icon={toggle.icon}
                            toggleKey={toggle.key}
                            value={insuranceDetails.insurance_covered}
                            handleClick={() => handleCoveredClick(toggle.key)}
                          />
                        ))}
                      </div>

                      {/* ── Beneficiary cards ── */}
                      {beneficiaries.map((beneficiary, index) => (
                        <BeneficiaryCard
                          key={beneficiary.id}
                          hideCard={
                            insuranceCheckLoading || sendPaymentLinkLoading
                          }
                          beneficiary={beneficiary}
                          index={index}
                          totalCount={beneficiaries.length}
                          onUpdate={(updates) =>
                            updateBeneficiary(beneficiary.id, updates)
                          }
                          onDelete={() => deleteBeneficiary(beneficiary.id)}
                          insuranceCovered={insuranceDetails.insurance_covered}
                          isAddrExpanded={!!addrExpandedIds[beneficiary.id]}
                          setIsAddrExpanded={(val) =>
                            setAddrExpandedIds((prev) => ({
                              ...prev,
                              [beneficiary.id]: val,
                            }))
                          }
                          _t={_t}
                        />
                      ))}

                      {/* ── Add beneficiary button ── */}
                      <button
                        type="button"
                        className="add-beneficiary-btn"
                        onClick={addBeneficiary}
                      >
                        + {_t("addAnotherBeneficiary")}
                      </button>

                      {/* ── Gran tenedor ── */}
                      <div
                        className="gran-tenedor-field d-flex align-items-center justify-content-between cursor-pointer"
                        onClick={() =>
                          setInsuranceDetails((prev) => ({
                            ...prev,
                            gran_tenedor: !prev.gran_tenedor,
                          }))
                        }
                      >
                        <div className="d-flex flex-column">
                          <span className="gran-tenedor-title">
                            {_t("granTenedor")}
                          </span>
                          <span className="gran-tenedor-subtitle">
                            {_t("granTenedorSubtitle")}
                          </span>
                        </div>
                        <div className="d-flex align-items-center custom-gap-2">
                          <span className="gran-tenedor-value-label">
                            {insuranceDetails.gran_tenedor
                              ? _t("yes")
                              : _t("no")}
                          </span>
                          <div
                            className={`gran-tenedor-toggle ${insuranceDetails.gran_tenedor ? "active" : ""}`}
                          >
                            <div className="gran-tenedor-thumb" />
                          </div>
                        </div>
                      </div>

                      {/* ── Dates ── */}
                      {insuranceDetails.insurance_covered !== "" && (
                        <div className="form-wrapper mt-0">
                          {[
                            {
                              name: "lease_start_date",
                              label: "leaseStartDate",
                            },
                            {
                              name: "policy_start_date",
                              label: "policyStartDate",
                            },
                          ].map(({ name, label }) => (
                            <div className="main-form-group" key={name}>
                              <div className="form-group">
                                <label>
                                  {_t(label)}{" "}
                                  <span className="required-star">*</span>
                                </label>
                                <input
                                  type="date"
                                  name={name}
                                  min={
                                    name === "policy_start_date" &&
                                    insuranceDetails.lease_start_date
                                      ? dateToInputValue(
                                          insuranceDetails.lease_start_date
                                        )
                                      : getTodayStr()
                                  }
                                  value={dateToInputValue(
                                    insuranceDetails[name]
                                  )}
                                  onChange={(e) =>
                                    setInsuranceDetails((prev) => ({
                                      ...prev,
                                      [name]: inputValueToDate(e.target.value),
                                    }))
                                  }
                                  className={`${!insuranceDetails[name] ? "input-error select-empty" : ""}`}
                                  required
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* end purchase-insurance-details-wrapper */}

                    {/* ── File upload area — hidden while checking ── */}
                    {!insuranceCheckLoading &&
                      !sendPaymentLinkLoading &&
                      (documentUploadLoading ? (
                        <div
                          className="doc-listing doc-listing2 d-flex align-items-center justify-content-center text-center"
                          style={{ height: 244 }}
                        >
                          <img
                            src={SkorDefaultLoader}
                            className="defaultSkorLoader"
                            alt="loader"
                          />
                        </div>
                      ) : (
                        <div
                          className={`doc-listing doc-listing2 doc-listing3 h-100 d-flex align-items-center justify-content-center text-center ${
                            !documentFiles.length && !invalidFiles.length
                              ? "cursor-pointer"
                              : ""
                          }`}
                          onClick={() => {
                            if (!documentFiles.length && !invalidFiles.length)
                              fileInputRef.current.click();
                          }}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragEnter}
                          onDragLeave={handleDragLeave}
                          style={{
                            backgroundColor: isDragging
                              ? "#A3EB64"
                              : "transparent",
                            transition: "background-color 0.2s ease",
                          }}
                        >
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={(e) => {
                              if (e.target.files?.length)
                                addFiles(e.target.files);
                            }}
                            style={{ display: "none" }}
                            accept="application/pdf,image/jpeg,image/png,image/jpg"
                          />

                          {documentFiles.length > 0 ||
                          invalidFiles.length > 0 ? (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                              }}
                            >
                              <div className="uploaded-list">
                                {/* Invalid files */}
                                {invalidFiles.map((file) => (
                                  <div
                                    key={file.id}
                                    className="d-flex uploaded-files-view align-items-center justify-content-between flex-wrap"
                                    style={{
                                      backgroundColor: "#fdf5f5",
                                      borderColor: "#f19492",
                                    }}
                                  >
                                    <div className="uploaded-files-view-name">
                                      <img
                                        src="/images/docIconGreen.svg"
                                        alt=""
                                      />
                                      <div className="upload-file-name">
                                        <h1
                                          className="mb-0 text-truncate"
                                          style={{ maxWidth: "250px" }}
                                        >
                                          {file.name}
                                        </h1>
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div
                                        style={{ color: "#dc2828" }}
                                        className="corrupted-upload-doucmentFiles"
                                      >
                                        <AlertIcon />
                                        <span>{file.reason}</span>
                                      </div>
                                      <div
                                        className="cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInvalidFiles((prev) =>
                                            prev.filter((f) => f.id !== file.id)
                                          );
                                        }}
                                      >
                                        <TrashIcon />
                                      </div>
                                    </div>
                                  </div>
                                ))}

                                {/* Valid files */}
                                {documentFiles.map((fileObj) => (
                                  <div
                                    key={fileObj.id}
                                    className="d-flex uploaded-files-view align-items-center justify-content-between flex-wrap"
                                  >
                                    <div className="uploaded-files-view-name">
                                      <img
                                        src="/images/docIconGreen.svg"
                                        alt=""
                                      />
                                      <div className="upload-file-name">
                                        <h1
                                          className="text-truncate"
                                          style={{ maxWidth: "250px" }}
                                        >
                                          {fileObj.name}
                                        </h1>
                                        <span>{formatSize(fileObj.size)}</span>
                                      </div>
                                    </div>
                                    <div
                                      className="cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDocumentFiles((prev) =>
                                          prev.filter(
                                            (f) => f.id !== fileObj.id
                                          )
                                        );
                                      }}
                                    >
                                      <TrashIcon />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="docUpload">
                              <div className="Uploadimgdiv">
                                <img src={Uploadimg} alt="upload-img" />
                              </div>
                              <p className="drag-drop-content pt-3">
                                {_t("dragAndDropYourRentalContract")}
                              </p>
                              <p className="spacepx">{_t("or")}</p>
                              <button
                                type="button"
                                className="btnNext btnSelectFile selectFilehere"
                              >
                                {_t("selectFromFiles")}
                              </button>
                              <p className="fileSizeText">
                                {_t("ensureTheSizeOfFileMustBeLessThan4Mb")}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                  {/* end faded fields div */}

                  {/* ── Checking insurability overlay ── */}
                  {(insuranceCheckLoading || sendPaymentLinkLoading) && (
                    <div className="insurance-checking-overlay">
                      <div className="insurance-checking-content">
                        {isSkorProtect ? (
                          <img
                            src={SkorProtectShield}
                            alt=""
                            className="skor-protect-checking-logo"
                          />
                        ) : (
                          <div className="insurance-checking-icon-circle">
                            <OutlineShieldIcon />
                          </div>
                        )}
                        <h3 className="insurance-checking-title">
                          {_t("checkingInsurability")}
                        </h3>
                        <p className="insurance-checking-subtitle">
                          {_t("checkingInsurabilitySubtitle")}
                        </p>
                        <div
                          className={`insurance-checking-dots${
                            isSkorProtect ? " skor-protect-dots" : ""
                          }`}
                        >
                          <span />
                          <span />
                          <span />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* end relative wrapper */}

                {/* ── Property errors ── */}
                {propertyErrors.length > 0 && (
                  <div
                    className="property-errors-container mt-3 mb-2"
                    style={{
                      padding: "10px 16px",
                      background: "#fff5f5",
                      border: "1px solid #f5c6c6",
                      borderRadius: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontWeight: 600,
                        color: "#c0392b",
                        fontSize: "13px",
                      }}
                      className="mb-1"
                    >
                      {_t("pleaseAddMissingDetailsForInsurance")}
                    </p>
                    <ul
                      className="mb-0 ps-3"
                      style={{ color: "#c0392b", fontSize: "13px" }}
                    >
                      {propertyErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* ── Consent loading indicator ── */}
                {consentLoading && (
                  <div className="d-flex align-items-center justify-content-center custom-gap-2 mt-3 mb-2">
                    <LoadingSpinner
                      size="0.8233vw"
                      color="#253837"
                      borderWidth={1}
                      className="docs-loading-spinner"
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#7f7f7f",
                      }}
                    >
                      {_t("checkingForConsents")}
                    </span>
                  </div>
                )}

                {/* ── Submit button ── */}
                <div className="row100 gridbar popup_btn2">
                  {!sendPaymentLinkLoading && (
                    <button
                      className="addGuarantorButton modal-btn btnNext"
                      type="submit"
                      onClick={checkForInsurance}
                      disabled={isSubmitDisabled || insuranceCheckLoading}
                    >
                      {isSkorProtect && insuranceCheckLoading
                        ? `${_t("uploadingContract")}...`
                        : isSkorProtect
                          ? _t("activateSkorProtect")
                          : redirectLoading
                            ? `${_t("redirecting")}...`
                            : insuranceCheckLoading
                              ? _t("checkingForInsurance")
                              : _t("checkForInsuranceEligibility")}
                    </button>
                  )}

                  {!insuranceCheckLoading &&
                    usedFor !== "skorProtect" &&
                    insuranceDetails.insurance_covered === "someoneElse" && (
                      <div className="send-payment-link-wrapper">
                        {!sendPaymentLinkLoading && (
                          <div className="send-payment-link-divider">
                            <span>{_t("or")}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          className="send-payment-link-btn"
                          onClick={handleSendPaymentLink}
                          disabled={isSubmitDisabled || sendPaymentLinkLoading}
                        >
                          <img
                            src="/images/send.svg"
                            alt="send"
                            className="send-payment-link-icon"
                          />
                          {sendPaymentLinkLoading
                            ? `${_t("checkingForInsuranceAndSendingPaymentLink")}...`
                            : _t("sendPaymentLink")}
                        </button>
                        {sendLinkError && (
                          <p className="send-payment-link-error">
                            {sendLinkError}
                          </p>
                        )}
                        <p className="send-payment-link-note">
                          <img
                            src="/images/clock.svg"
                            alt="clock"
                            className="send-payment-link-clock-icon"
                          />
                          {_t("sendPaymentLinkNote")}
                        </p>
                      </div>
                    )}
                </div>
              </form>
            </div>
          </Modal.Body>
        </Modal>
      ) : (
        <InsuranceReviewModal
          show={showInsuranceReviewModal}
          onHide={() => setShowInsuranceReviewModal(false)}
          onGotIt={() => {
            setShowInsuranceReviewModal(false);
          }}
        />
      )}
    </>
  );
};

export default PurchaseInsurancePopup;
